import { AmountInfoBoxLocalized, InfoBoxBadgeLabelWithTooltip, Translate } from '@components'

import { AmountInfoBoxesGroup, Space, Text } from '@billy/design-system'
import { ReactElement, ReactNode, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { quoteListFilterToBadgeColor } from '@features/invoices/constants/quoteListFilterToBadgeVariant'
import { QuoteListFilter } from '@features/invoices/enums/quoteListFilter'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QUOTES_SUMMARY_FILTERS_ORDER } from '../../constants/quotesSummaryFiltersOrder'
import { useQuotesListFilters } from '../../context/quotesListFilters'
import { useQuotesSummaryFiltered } from '../../hooks/useQuotesSummaryFiltered'
import { SummaryInfoBox } from './types'

const quoteListFilterToTitleKey: Record<QuoteListFilter, string> = {
  [QuoteListFilter.All]: 'quotes.list.filter.all',
  [QuoteListFilter.Draft]: 'quotes.list.filter.drafts',
  [QuoteListFilter.Pending]: 'quotes.list.filter.pending',
  [QuoteListFilter.Accepted]: 'quotes.list.filter.accepted',
  [QuoteListFilter.Rejected]: 'quotes.list.filter.declined',
  [QuoteListFilter.Invoiced]: 'quotes.list.filter.invoiced',
}

export const QuotesSummary = (): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { data, isLoading } = useQuotesSummaryFiltered()
  const [{ status: currentStatus }, setFilters] = useQuotesListFilters()

  const handleClick = useCallback(
    (id: QuoteListFilter) => {
      setFilters({ page: 1, status: id })
    },
    [setFilters],
  )

  const infoBoxes: SummaryInfoBox[] = useMemo(
    () =>
      QUOTES_SUMMARY_FILTERS_ORDER.map((status) => {
        const { amount, count: badgeAmount } = data?.[status] || {}
        const label = t(quoteListFilterToTitleKey[status], { count: badgeAmount })
        const badgeLabel: ReactNode =
          status === QuoteListFilter.All ? (
            <InfoBoxBadgeLabelWithTooltip label={label} tooltipLabel={t('quotes.list.filter.all.tooltip')} />
          ) : (
            label
          )

        return {
          active: status === currentStatus,
          amount,
          badgeAmount,
          badgeLabel,
          badgeColor: quoteListFilterToBadgeColor[status],
          id: status,
        }
      }),
    [currentStatus, data, t],
  )

  return (
    <>
      <Text variant="h4">
        <Translate value="quotes.list.summary.title" />
      </Text>
      <Space size="s16" />
      <AmountInfoBoxesGroup itemsInRow={infoBoxes.length} loading={isLoading}>
        {infoBoxes.map((infoBox) => (
          <AmountInfoBoxLocalized
            active={infoBox.active}
            amount={infoBox.amount}
            badgeAmount={infoBox.badgeAmount}
            badgeLabel={infoBox.badgeLabel}
            badgeColor={infoBox.badgeColor}
            clickable
            currencyId={organization?.baseCurrencyId}
            id={infoBox.id}
            key={infoBox.id}
            onClick={handleClick}
            shortFormat={false}
          />
        ))}
      </AmountInfoBoxesGroup>
      <Space size="s32" />
    </>
  )
}
